import { defineStore } from 'pinia'
import { ref } from 'vue'

/**
 * - post-publish: opened once after a trial project's first successful publish.
 * - keep: opened from the sidebar trial status, Settings → Billing, Upgrade, the
 *   last-days publish row or the expired dashboard card. Its header follows the
 *   current trial state (trialing, last days, today, expired).
 * - project-setup: a project with no subscription yet (new project billing setup).
 */
export type BillingDialogMode = 'post-publish' | 'keep' | 'project-setup'

export type BillingDialogSource =
  | 'post-publish'
  | 'sidebar'
  | 'settings-billing'
  | 'upgrade'
  | 'publish-row'
  | 'expired-dashboard'
  | 'project-setup'
  | 'publish'

export type BillingDialogOpenOptions = {
  mode: BillingDialogMode
  source: BillingDialogSource
  /** Preselect this Stripe price (Upgrade page plan buttons). */
  initialPriceId?: string | null
  /** Hide close affordances (new project billing setup). */
  blocking?: boolean
  /** Runs once after a successful activation, after the dialog has closed. */
  onSuccess?: () => void | Promise<void>
}

export const useBillingDialogStore = defineStore('billingDialog', () => {
  const visible = ref(false)
  const mode = ref<BillingDialogMode>('keep')
  const source = ref<BillingDialogSource>('sidebar')
  const initialPriceId = ref<string | null>(null)
  const blocking = ref(false)
  const onSuccess = ref<(() => void | Promise<void>) | null>(null)

  function open(options: BillingDialogOpenOptions) {
    mode.value = options.mode
    source.value = options.source
    initialPriceId.value = options.initialPriceId ?? null
    blocking.value = options.blocking ?? false
    onSuccess.value = options.onSuccess ?? null
    visible.value = true
  }

  function close() {
    visible.value = false
    blocking.value = false
    onSuccess.value = null
  }

  return { visible, mode, source, initialPriceId, blocking, onSuccess, open, close }
})
